'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';

export type TierAccess = { free: boolean; pro: boolean; full: boolean };

export interface FeatureFlags {
  proSubscriptions: TierAccess;
  advancedCharts: TierAccess;
  stockScreener: TierAccess;
  earningsCalendar: TierAccess;
  portfolioTracker: TierAccess;
  priceAlerts: TierAccess;
  apiAccess: TierAccess;
  exportData: TierAccess;
  intradayChart: TierAccess;
  financials: TierAccess;
  financialHealthSummary: TierAccess;
}

interface PageAccessRule {
  path: string;
  label: string;
  access: 'public' | 'registered' | 'pro' | 'full';
}

export interface MarketingSettings {
  mode: 'original' | 'carousel';
  carouselInterval: number;
}

/** Per-emitter global on/off state for scanner qualification signals.
 *  Keyed by scanner_reason. False = globally disabled in admin
 *  settings — auto-trade portfolio UI hides those signals from the
 *  per-portfolio chip + dropdown so users can't enable a path that's
 *  globally turned off. */
export type ScannerEmitterFlags = Record<string, boolean>;

interface SiteState {
  siteName: string;
  logoUrl: string | null;
  /** Optional icon-only variant for narrow viewports. Null = use logoUrl
   *  on every size. */
  logoUrlMobile: string | null;
  tagline: string;
  brandingLoaded: boolean;
  features: FeatureFlags;
  featuresLoaded: boolean;
  pageAccess: PageAccessRule[];
  pageAccessLoaded: boolean;
  marketing: MarketingSettings;
  marketingLoaded: boolean;
  scannerEmitters: ScannerEmitterFlags;
  scannerEmittersLoaded: boolean;
}

const DEFAULT_FEATURES: FeatureFlags = {
  proSubscriptions: { free: true,  pro: true,  full: true },
  advancedCharts:   { free: false, pro: true,  full: true },
  stockScreener:    { free: true,  pro: true,  full: true },
  earningsCalendar: { free: true,  pro: true,  full: true },
  portfolioTracker: { free: false, pro: false, full: false },
  priceAlerts:      { free: false, pro: false, full: false },
  apiAccess:        { free: false, pro: false, full: true },
  exportData:       { free: false, pro: true,  full: true },
  intradayChart:    { free: false, pro: true,  full: true },
  financials:       { free: false, pro: true,  full: true },
  financialHealthSummary: { free: false, pro: true,  full: true },
};

/** Normalize legacy boolean flags to TierAccess */
function normalizeFeatureFlags(raw: Record<string, unknown>): FeatureFlags {
  const result: Record<string, TierAccess> = {};
  for (const [key, def] of Object.entries(DEFAULT_FEATURES)) {
    const val = raw[key];
    if (val === undefined || val === null) {
      result[key] = def as TierAccess;
    } else if (typeof val === 'boolean') {
      result[key] = { free: val, pro: val, full: val };
    } else if (typeof val === 'object' && val !== null && 'free' in val) {
      result[key] = val as TierAccess;
    } else {
      result[key] = def as TierAccess;
    }
  }
  return result as unknown as FeatureFlags;
}

const DEFAULT: SiteState = {
  siteName: 'StocksIQPro',
  logoUrl: null,
  logoUrlMobile: null,
  tagline: 'Free Financial Data',
  brandingLoaded: false,
  features: DEFAULT_FEATURES,
  featuresLoaded: false,
  pageAccess: [],
  pageAccessLoaded: false,
  marketing: { mode: 'original', carouselInterval: 5 },
  marketingLoaded: false,
  // Default to "everything enabled" until the public fetch resolves.
  // Means a slow first paint shows ALL signals on the auto-trade
  // portfolio settings page — the filter narrows down once flags land,
  // never the other way around (which would briefly hide signals
  // the user already had enabled).
  scannerEmitters: {},
  scannerEmittersLoaded: false,
};

const SiteContext = createContext<SiteState>(DEFAULT);

export function SiteProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SiteState>(DEFAULT);

  useEffect(() => {
    // Fetch branding
    fetch('/api/admin/public/branding')
      .then(r => r.json())
      .then(j => {
        setState(s => ({ ...s, ...(j?.data?.siteName ? j.data : {}), brandingLoaded: true }));
      })
      .catch(() => {
        setState(s => ({ ...s, brandingLoaded: true }));
      });

    // Fetch feature flags
    fetch('/api/admin/public/features')
      .then(r => r.json())
      .then(j => {
        if (j?.data) setState(s => ({ ...s, features: normalizeFeatureFlags(j.data), featuresLoaded: true }));
        else setState(s => ({ ...s, featuresLoaded: true }));
      })
      .catch(() => {
        setState(s => ({ ...s, featuresLoaded: true }));
      });

    // Fetch page access rules
    fetch('/api/admin/public/page-access')
      .then(r => r.json())
      .then(j => {
        if (j?.data && Array.isArray(j.data)) setState(s => ({ ...s, pageAccess: j.data, pageAccessLoaded: true }));
        else setState(s => ({ ...s, pageAccessLoaded: true }));
      })
      .catch(() => {
        setState(s => ({ ...s, pageAccessLoaded: true }));
      });

    // Fetch marketing tile settings
    fetch('/api/admin/public/marketing')
      .then(r => r.json())
      .then(j => {
        if (j?.data) setState(s => ({ ...s, marketing: { ...s.marketing, ...j.data }, marketingLoaded: true }));
        else setState(s => ({ ...s, marketingLoaded: true }));
      })
      .catch(() => {
        setState(s => ({ ...s, marketingLoaded: true }));
      });

    // Fetch per-emitter scanner enable flags — drives the auto-trade
    // portfolio settings filter so globally-disabled qualification
    // signals (confluence_stack with admin-side enableConfluenceStack
    // off, pressure with enablePressureQualifier off, etc.) don't
    // appear in the per-portfolio chip list or "+ Add signal" dropdown.
    fetch('/api/admin/public/scanner-emitters')
      .then(r => r.json())
      .then(j => {
        if (j?.data && typeof j.data === 'object') {
          setState(s => ({ ...s, scannerEmitters: j.data as ScannerEmitterFlags, scannerEmittersLoaded: true }));
        } else {
          setState(s => ({ ...s, scannerEmittersLoaded: true }));
        }
      })
      .catch(() => {
        setState(s => ({ ...s, scannerEmittersLoaded: true }));
      });
  }, []);

  return <SiteContext.Provider value={state}>{children}</SiteContext.Provider>;
}

export function useSite() {
  return useContext(SiteContext);
}

/** Returns the per-emitter scanner enable flags + loaded marker.
 *  `isEmitterEnabled(key)` resolves to true unless the global admin
 *  toggle has explicitly switched the emitter off; an unknown key
 *  defaults to true (treat new emitters as available rather than
 *  silently hiding them when the server hasn't seen them yet). */
export function useScannerEmitters(): { isEmitterEnabled: (key: string) => boolean; loaded: boolean } {
  const { scannerEmitters, scannerEmittersLoaded } = useContext(SiteContext);
  const isEmitterEnabled = useCallback(
    (key: string) => scannerEmitters[key] !== false,
    [scannerEmitters],
  );
  return { isEmitterEnabled, loaded: scannerEmittersLoaded };
}

/** Returns feature flags resolved to booleans for the current user's tier */
export function useFeatures(): Record<keyof FeatureFlags, boolean> & { loaded: boolean } {
  const { features, featuresLoaded } = useContext(SiteContext);
  const { user } = useAuth();
  const rawTier = user?.subscriptionTier ?? 'free';
  const tier: 'free' | 'pro' | 'full' = rawTier === 'vip' ? 'full' : rawTier as 'free' | 'pro' | 'full';

  const resolved = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const [key, val] of Object.entries(features)) {
      if (typeof val === 'boolean') out[key] = val;
      else out[key] = val?.[tier] ?? false;
    }
    return out;
  }, [features, tier]);

  return { ...resolved, loaded: featuresLoaded } as Record<keyof FeatureFlags, boolean> & { loaded: boolean };
}

/** Returns raw TierAccess feature flags (for admin grid) */
export function useRawFeatures(): { features: FeatureFlags; loaded: boolean } {
  const { features, featuresLoaded } = useContext(SiteContext);
  return { features, loaded: featuresLoaded };
}

export function usePageAccess(): { getAccess: (path: string) => 'public' | 'registered' | 'pro' | 'full' | 'admin'; loaded: boolean } {
  const { pageAccess, pageAccessLoaded } = useContext(SiteContext);

  const getAccess = useCallback((path: string): 'public' | 'registered' | 'pro' | 'full' | 'admin' => {
    // Strip query string and trailing slash for matching
    const cleanPath = path.split('?')[0].replace(/\/$/, '') || '/';

    // Exact match first
    const exact = pageAccess.find(r => r.path === cleanPath);
    if (exact) return exact.access;

    // Try parent paths (e.g. /stocks/AAPL -> /stocks)
    const segments = cleanPath.split('/').filter(Boolean);
    for (let i = segments.length - 1; i >= 1; i--) {
      const parent = '/' + segments.slice(0, i).join('/');
      const parentRule = pageAccess.find(r => r.path === parent);
      if (parentRule) return parentRule.access;
    }

    // Default to public
    return 'public';
  }, [pageAccess]);

  return { getAccess, loaded: pageAccessLoaded };
}
